<script setup lang="ts">
/**
 * Every way data comes in. add: new files for a project's documents or its datasets, or one of the
 * project's connectors — the connector lists its objects on the next step (ConnectorPickDialog) and
 * nothing is copied before the pick. replace: one file that replaces the entity's current file and
 * mints a new revision; the entity keeps its name and id.
 * POST /projects/{id}/data-objects · POST /datasets · connectors from GET /connectors.
 */
import Dialog from '~/components/ui/Dialog.vue'
import Choice from '~/components/ui/Choice.vue'
import PickList from '~/components/ui/PickList.vue'
import Icon from '~/components/ui/Icon.vue'
import ActionButton from '~/components/ui/ActionButton.vue'
import ActionLink from '~/components/ui/ActionLink.vue'
import { formatBytes } from '~/utils/dataObjects'

const props = withDefaults(defineProps<{
  connectors?: { id: string; name: string; facts: string }[]
  /** Accepted formats: a comma list of extensions, or words when the server decides by content. */
  formats: string
  mode?: 'add' | 'replace'
  kind?: 'data' | 'dataset'
  /** The entity a replacement is for; the header names it. */
  entity?: string
}>(), { connectors: () => [], mode: 'add', kind: 'data', entity: '' })
const emit = defineEmits<{ close: []; submit: [payload: { files: File[]; source: string }] }>()
const source = ref('upload')
const files = ref<File[]>([])
const fileInput = ref<HTMLInputElement | null>(null)
const single = computed(() => props.mode === 'replace')
const extensions = computed(() => props.formats.split(',').map((entry) => entry.trim()).filter((entry) => entry.startsWith('.')))
const shown = computed(() => extensions.value.length ? extensions.value.map((entry) => entry.slice(1)).join(' · ') : props.formats)
const title = computed(() => single.value ? 'Replace file' : props.kind === 'dataset' ? 'New dataset' : 'Add data')
const meta = computed(() => single.value ? props.entity : props.connectors.length ? 'files or a connector' : 'files')
function take(list: FileList | null | undefined) {
  const chosen = Array.from(list ?? [])
  files.value = single.value ? chosen.slice(0, 1) : [...files.value, ...chosen.filter((file) => !files.value.some((entry) => entry.name === file.name && entry.size === file.size))]
  if (fileInput.value) fileInput.value.value = ''
}
const choose = (event: Event) => take((event.target as HTMLInputElement).files)
const drop = (event: DragEvent) => take(event.dataTransfer?.files)
const remove = (file: File) => { files.value = files.value.filter((entry) => entry !== file) }
const ready = computed(() => source.value !== 'upload' || files.value.length > 0)
</script>

<template>
  <Dialog :title="title" :meta="meta" :width="540" @close="emit('close')">
    <template #icon><Icon :name="single ? 'refresh' : kind === 'dataset' ? 'datasets' : 'data'" /></template>

    <div v-if="!single && connectors.length" class="flex flex-col gap-1.5">
      <Choice title="Upload files" :facts="shown" :selected="source === 'upload'" @click="source = 'upload'" />
      <PickList v-slot="{ shown: list }" :items="connectors" unit="connectors" height="var(--measure-160px)">
        <Choice v-for="connector in list" :key="connector.id" :title="connector.name" :facts="connector.facts" :selected="source === connector.id" @click="source = connector.id" />
      </PickList>
    </div>

    <div v-if="source === 'upload'" class="flex flex-col items-center gap-2 rounded-md border border-border bg-muted/20 px-3 py-6" @dragover.prevent @drop.prevent="drop">
      <Icon name="upload" :size="16" />
      <span class="text-xs">{{ single ? 'Drop the new file here, or choose it' : 'Drop files here, or choose them' }}</span>
      <span class="num text-[length:var(--type-fine)] text-muted-foreground">{{ shown }}</span>
      <input ref="fileInput" type="file" :multiple="!single" :accept="extensions.join(',') || undefined" class="hidden" @change="choose">
      <ActionLink :label="single ? 'Choose a file' : 'Choose files'" @click="fileInput?.click()"><template #icon><Icon name="folder" /></template></ActionLink>
    </div>
    <p v-else class="num m-0 rounded-md border border-border bg-muted/20 px-2.5 py-2 text-[length:var(--type-meta)] leading-relaxed text-muted-foreground">
      The connector lists its objects on the next step; nothing is copied until you pick them.
    </p>

    <div v-if="files.length" class="flex flex-col rounded-md border border-border">
      <div class="flex h-7 items-center gap-2 border-b border-border px-2.5">
        <span class="num text-[length:var(--type-fine)] text-muted-foreground">{{ files.length }} files · {{ formatBytes(files.reduce((sum, file) => sum + file.size, 0)) }}</span>
        <span class="flex-1" />
        <ActionLink v-if="files.length > 1" label="Remove all" variant="muted" @click="files.splice(0)"><template #icon><Icon name="x" /></template></ActionLink>
      </div>
      <div class="flex max-h-[var(--measure-160px)] flex-col gap-[var(--measure-7px)] overflow-auto p-2.5">
        <div v-for="file in files" :key="file.name + file.size" class="flex items-center gap-2 text-xs">
          <Icon name="file" :size="12" class="shrink-0 text-muted-foreground" />
          <span class="min-w-0 flex-1 truncate">{{ file.name }}</span>
          <span class="num shrink-0 text-[length:var(--type-meta)] text-muted-foreground">{{ formatBytes(file.size) }}</span>
          <ActionLink label="Remove" variant="muted" @click="remove(file)"><template #icon><Icon name="x" /></template></ActionLink>
        </div>
      </div>
    </div>
    <p v-if="single" class="num m-0 text-[length:var(--type-fine)] text-muted-foreground">The new file becomes a new revision · the name and the id do not change</p>

    <template #actions>
      <ActionButton label="Cancel" @click="emit('close')" />
      <ActionButton v-if="source === 'upload'" :label="single ? 'Replace file' : kind === 'dataset' ? 'Create dataset' : 'Add data'" variant="primary" :disabled="!ready" @click="emit('submit', { files, source })">
        <template #icon><Icon :name="single ? 'refresh' : 'plus'" /></template>
      </ActionButton>
      <ActionButton v-else label="Choose what to import" variant="primary" @click="emit('submit', { files: [], source })">
        <template #trail><Icon name="arrow-right" /></template>
      </ActionButton>
    </template>
  </Dialog>
</template>
